import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Table, Tag, Button, Space, List, Select, Input, Tooltip } from 'antd';
import { EyeOutlined, EditOutlined, FileOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { columnLabels, STATUSES, FAILURE_REASONS } from '../../constants/candidateColumnLabels';
import StatusTag from '../StatusTag';
import { canEditCandidate as canEditCandidatePerm } from '../../utils/candidatePermissions';

const createStatusOptions = (funnelStatuses) => {
  if (!funnelStatuses || funnelStatuses.length === 0) return [];

  return funnelStatuses
    .filter(status => status && status.id !== undefined && status.id !== null)
    .map(status => ({
      value: status.id.toString(),
      label: status.name || 'Без названия'
    }));
};

const CandidateList = ({
  candidates,
  loading,
  onUpdate,
  onDelete,
  onView,
  onSelect,
  visibleColumns,
  isSelectMode = false,
  getFiles,
  loadVacancyData = true,
  candidateVacancyData = null,
  isVacancyContext = false,
  currentVacancy = null,
  funnelConfig = null,
  onStatusChange,
  onReturnToWork,
  pageSize = 10,
  showSizeChanger = true,
  showQuickJumper = true,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  filters: externalFilters,
  onFiltersChange,
  isModalMode = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCandidatesPage = location.pathname.includes('/candidates');
  const { funnelStatuses, loading: funnelStatusesLoading } = useSelector((state) => state.vacancies);
  const { role: roleFromStore } = useSelector((state) => state.auth);
  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = roleFromStore || storedRole;
  const canEditCandidate = canEditCandidatePerm(effectiveRole).canEdit;
  const [localFilters, setLocalFilters] = useState({});
  const filters = externalFilters !== undefined ? externalFilters : localFilters;
  const setFilters = onFiltersChange || setLocalFilters;
  const [tempFilters, setTempFilters] = useState({});
  const [displayedCount, setDisplayedCount] = useState(10);
  const [modalFilters, setModalFilters] = useState({});

  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (isModalMode) {
      setDisplayedCount(10);
      setModalFilters({});
    }
  }, [candidates, isModalMode]);

  const handleShowMore = useCallback(() => {
    setDisplayedCount(prev => prev + 10);
  }, []);

  // Применяем фильтры к данным в модальном режиме
  const filteredCandidates = useMemo(() => {
    if (!isModalMode) return Array.isArray(candidates) ? candidates : [];

    let filtered = Array.isArray(candidates) ? candidates : [];

    // Применяем фильтры из modalFilters
    Object.keys(modalFilters).forEach(key => {
      const filterValue = modalFilters[key];
      if (filterValue !== null && filterValue !== undefined && filterValue !== '') {
        filtered = filtered.filter(record => {
          if (key === 'status') {
            // Для статуса нужно проверить статус кандидата в вакансии
            if (isVacancyContext && currentVacancy && candidateVacancyData) {
              const candidateVacancy = candidateVacancyData.find(cv => cv.candidate_id === record.id);
              if (candidateVacancy) {
                return String(candidateVacancy.status_id) === String(filterValue);
              }
            }
            // Для обычного режима проверяем статус из vacancies
            if (record.vacancies && Array.isArray(record.vacancies) && record.vacancies.length > 0) {
              return String(record.vacancies[0].status_id) === String(filterValue);
            }
            return false;
          }
          if (key === 'completion_work') {
            // Для даты завершения нужно получить правильное значение
            let dateValue = record[key];
            if (isVacancyContext && currentVacancy && candidateVacancyData) {
              const candidateVacancy = candidateVacancyData.find(vc =>
                vc.candidate_id === record.id && vc.vacancy_id === currentVacancy.id
              );
              if (candidateVacancy?.completion_work) {
                dateValue = candidateVacancy.completion_work;
              }
            }
            if (!dateValue) return false;
            // Форматируем дату для поиска
            const formattedDate = new Date(dateValue).toLocaleDateString('ru-RU');
            return formattedDate.toLowerCase().includes(String(filterValue).toLowerCase());
          }
          const cellValue = record[key];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase());
        });
      }
    });

    return filtered;
  }, [isModalMode, candidates, modalFilters, isVacancyContext, currentVacancy, candidateVacancyData]);

  const displayedCandidates = isModalMode
    ? (Array.isArray(filteredCandidates) ? filteredCandidates.slice(0, displayedCount) : [])
    : (Array.isArray(candidates) ? candidates : []);

  const hasMoreCandidates = isModalMode && Array.isArray(filteredCandidates) && displayedCount < filteredCandidates.length;

  const getVacancyName = useCallback((id) => {
    if (candidates && candidates.length > 0) {
      for (const candidate of candidates) {
        if (candidate.vacancies && Array.isArray(candidate.vacancies)) {
          const vacancy = candidate.vacancies.find(v => v.vacancy_id === id);
          if (vacancy) {
            return vacancy.job_title || 'Не указана';
          }
        }
      }
    }

    return 'Не указана';
  }, [candidates]);

  const getStatusName = useCallback((statusId) => {
    if (!funnelStatuses || funnelStatuses.length === 0) {
      return `Статус ${statusId}`;
    }

    const status = funnelStatuses.find(s => s.id === parseInt(statusId));
    return status ? `${status.name}` : `Статус ${statusId}`;
  }, [funnelStatuses]);

  const getStatusKeyFromName = (statusName) => {
    if (statusName.includes('Новый')) return 'new';
    if (statusName.includes('Перезвонить')) return 'call_back';
    if (statusName.includes('Собеседование')) return 'interview';
    if (statusName.includes('Сбор рекомендаций')) return 'references';
    if (statusName.includes('Предложение о работе')) return 'offer';
    if (statusName.includes('Оформление документов')) return 'documentation';
    if (statusName.includes('Трудоустроен') || statusName.includes('Завершить')) return 'hired';
    if (statusName.includes('Отклонён') || statusName.includes('Отказ')) return 'failed';
    return null;
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const newTempFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key].length > 0) {
        newTempFilters[key] = key === 'status' ? filters[key] : filters[key][0];
      }
    });
    setTempFilters(newTempFilters);
  };

  const handleApplyFilters = useCallback(() => {
    setFilters(tempFilters);
  }, [tempFilters, setFilters]);

  const handleResetFilters = useCallback(() => {
    setTempFilters({});
    setFilters({});
  }, [setFilters]);

  const hasTempFiltersForTitle = Object.keys(tempFilters).length > 0;

  const generateColumns = useMemo(() => {
    if (!visibleColumns) return [];

    const statusOptions = createStatusOptions(funnelStatuses);

    const actionColumn = {
      title: isModalMode ? 'Действия' : (
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleApplyFilters}
          disabled={!hasTempFiltersForTitle}
          size="small"
        >
          Найти
        </Button>
      ),
      key: 'actions',
      width: isCandidatesPage ? 80 : 120,
      style: {
        wordBreak: 'keep-all',
        whiteSpace: 'nowrap'
      },
      onCell: () => ({
        style: {
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap'
        },
        className: 'actions-column'
      }),
      render: (_, record) => {
        if (isSelectMode) {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => onSelect(record.id)}
            >
              Выбрать
            </Button>
          );
        }
        return isCandidatesPage ? (
          <Space size="small">
            <Tooltip title="Просмотреть кандидата">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => navigate(`/candidates/${record.id}`)}
              />
            </Tooltip>

            <Tooltip title={canEditCandidate ? 'Редактировать кандидата' : 'Нет прав для редактирования кандидата'}>
              <Button
                icon={<EditOutlined />}
                size="small"
                disabled={!canEditCandidate}
                onClick={() => onUpdate && onUpdate(record)}
              />
            </Tooltip>

            {/* {
              onDelete && (
                <Tooltip title="Удалить кандидата">
                  <Popconfirm
                    title="Удалить заявку?"
                    onConfirm={() => onDelete(record.id)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <Button size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                </Tooltip>
              )
            } */}
          </Space >
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Link to={`/candidates/${record.id}`}>
              Перейти
            </Link>
            {(() => {
              if (isVacancyContext && currentVacancy && candidateVacancyData) {
                const candidateVacancy = candidateVacancyData.find(cv =>
                  cv.candidate_id === record.id && cv.vacancy_id === currentVacancy.id
                );

                if (candidateVacancy?.status_id === 11 && onReturnToWork) {
                  return (
                    <Button
                      type="link"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => onReturnToWork(record.id)}
                    >
                      Вернуть в работу
                    </Button>
                  );
                }
              }
              return null;
            })()}
          </div>
        );
      }
    };

    const columnRenderMap = {
      full_name: (text) => text || 'Не указано',
      phone_number: (text) => text || 'Не указано',
      vacancy_id: (id) => getVacancyName(id),
      salary_range: (text) => text || 'Не указан',
      status: (status, record) => {
        if (record.vacancies && Array.isArray(record.vacancies) && record.vacancies.length > 0) {
          const firstVacancy = record.vacancies[0];
          const statusName = getStatusName(firstVacancy.status_id);
          const statusKey = getStatusKeyFromName(statusName);

          const statusElement = statusKey && STATUSES[statusKey] ?
            <StatusTag status={statusKey} statuses={STATUSES} /> :
            <Tag color="default">{statusName}</Tag>;

          // Добавляем результат кандидата если есть
          if (firstVacancy.result_candidate) {
            const resultTag = firstVacancy.result_candidate === 'success' ?
              <Tag color="green" size="small">Успех</Tag> :
              firstVacancy.result_candidate === 'failure' ?
                <Tag color="red" size="small">Провал</Tag> :
                <Tag color="blue" size="small">В резерве</Tag>;

            return (
              <div>
                {statusElement}
                {resultTag}
              </div>
            );
          }

          return statusElement;
        }

        if (candidateVacancyData) {
          const candidateVacancy = candidateVacancyData.find(cv => cv.candidate_id === record.id);
          if (candidateVacancy) {
            const statusName = getStatusName(candidateVacancy.status_id);
            const statusKey = getStatusKeyFromName(statusName);

            const statusElement = statusKey && STATUSES[statusKey] ?
              <StatusTag status={statusKey} statuses={STATUSES} /> :
              <Tag color="default">{statusName}</Tag>;

            // Добавляем результат кандидата если есть
            if (candidateVacancy.result_candidate) {
              const resultTag = candidateVacancy.result_candidate === 'success' ?
                <Tag color="green" size="small">Успех</Tag> :
                candidateVacancy.result_candidate === 'failure' ?
                  <Tag color="red" size="small">Провал</Tag> :
                  <Tag color="blue" size="small">В резерве</Tag>;

              return (
                <div>
                  {statusElement}
                  {resultTag}
                </div>
              );
            }

            return statusElement;
          }
        }

        const statusName = getStatusName(status);
        const statusKey = getStatusKeyFromName(statusName);

        if (statusKey && STATUSES[statusKey]) {
          return <StatusTag status={statusKey} statuses={STATUSES} />;
        }

        return <Tag color="default">{statusName}</Tag>;
      },
      completion_work: (completionWork, record) => {
        if (!isVacancyContext) return completionWork || 'Не указано';

        const candidateVacancy = candidateVacancyData?.find(vc =>
          vc.candidate_id === record.id && vc.vacancy_id === currentVacancy.id
        );

        const date = candidateVacancy?.completion_work || completionWork;

        if (!date) return 'Не указано';

        return new Date(date).toLocaleDateString('ru-RU');
      },
      failure_reason: (failureReason, record) => {
        if (!isVacancyContext) return failureReason || 'Не указано';

        const candidateVacancy = candidateVacancyData?.find(vc =>
          vc.candidate_id === record.id && vc.vacancy_id === currentVacancy.id
        );

        const reason = candidateVacancy?.failure_reason || failureReason;

        if (!reason) return 'Не указано';


        return FAILURE_REASONS[reason] || reason;
      },
      files: (_, record) => {
        const truncateFileName = (fileName, maxLength = 20) => {
          if (!fileName) return 'Файл';
          if (fileName.length <= maxLength) return fileName;

          const lastDotIndex = fileName.lastIndexOf('.');
          const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
          const nameWithoutExt = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;

          const maxNameLength = maxLength - extension.length - 3; // 3 для "..."

          if (maxNameLength <= 0) {
            return '...' + extension;
          }

          return nameWithoutExt.substring(0, maxNameLength) + '...' + extension;
        };

        if (record.files && Array.isArray(record.files)) {
          const files = record.files;

          if (files.length === 0) {
            return <span>Нет файлов</span>;
          }

          if (files.length === 1) {
            return (
              <Tooltip title={files[0].name || 'Файл'}>
                <Button
                  type="link"
                  icon={<FileOutlined />}
                  size="small"
                  onClick={() => window.open('https://dev.1ak-group.com/service/static/' + files[0].path, '_blank')}
                  style={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  {truncateFileName(files[0].name)}
                </Button>
              </Tooltip>
            );
          }

          const displayFiles = files.slice(0, 3);
          const hasMoreFiles = files.length > 3;

          return (
            <div>
              <List
                size="small"
                dataSource={displayFiles}
                renderItem={file => (
                  <List.Item style={{ padding: 0 }}>
                    <Tooltip title={file.name || 'Файл'}>
                      <Button
                        type="link"
                        icon={<FileOutlined />}
                        size="small"
                        onClick={() => window.open('https://dev.1ak-group.com/service/static/' + file.path, '_blank')}
                        style={{
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}
                      >
                        {truncateFileName(file.name)}
                      </Button>
                    </Tooltip>
                  </List.Item>
                )}
              />
              {hasMoreFiles && (
                <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                  и еще {files.length - 3} файл(ов)
                </div>
              )}
            </div>
          );
        }

        return <span>Нет файлов</span>;
      },
      vacancy_and_status: (_, record) => {
        if (isVacancyContext && currentVacancy) {
          if (funnelStatusesLoading) {
            return <span>Загрузка...</span>;
          }

          const vacancyStatuses = funnelConfig || [];

          if (!vacancyStatuses || vacancyStatuses.length === 0) {
            return <span>Воронка не настроена</span>;
          }

          const candidateVacancy = candidateVacancyData?.find(cv =>
            cv.candidate_id === record.id && cv.vacancy_id === currentVacancy.id
          );

          if (!candidateVacancy) {
            return <span>Нет данных о статусе</span>;
          }

          const statusOptions = vacancyStatuses
            .filter(status => status && status.status_id !== undefined && status.status_id !== null)
            .map(status => {
              let label = status.status_name || 'Без названия';
              const statusKey = getStatusKeyFromName(label);

              // Если это текущий статус кандидата и он "Завершить", показываем результат
              if (statusKey === 'hired' &&
                candidateVacancy.status_id === status.status_id &&
                candidateVacancy.result_candidate) {
                if (candidateVacancy.result_candidate === 'success') {
                  label = 'Успех';
                } else if (candidateVacancy.result_candidate === 'failure') {
                  label = 'Провал';
                } else if (candidateVacancy.result_candidate === 'reserved') {
                  label = 'В резерве';
                }
              }

              return {
                value: status.status_id.toString(),
                label: label
              };
            });

          return (
            <Select
              style={{ width: '100%', minWidth: 200 }}
              value={candidateVacancy.status_id ? candidateVacancy.status_id.toString() : undefined}
              options={statusOptions}
              placeholder="Выберите статус"
              onChange={(value) => {
                // if (onStatusChange) {
                onStatusChange({ id: record.id, vacancy_id: currentVacancy.id }, value);
                // }
              }}
            />
          );
        }

        if (record.vacancies && Array.isArray(record.vacancies) && record.vacancies.length > 0) {
          if (funnelStatusesLoading) {
            return <span>Загрузка...</span>;
          }

          if (!funnelStatuses || funnelStatuses.length === 0) {
            return <span>Статусы не загружены</span>;
          }

          const vacancyOptions = record.vacancies.map(vacancy => {
            const vacancyName = vacancy.job_title || 'Не указана';
            let statusName = getStatusName(vacancy.status_id);
            const statusKey = getStatusKeyFromName(statusName);

            // Если статус "Завершить", показываем результат кандидата
            if (statusKey === 'hired' && vacancy.result_candidate) {
              if (vacancy.result_candidate === 'success') {
                statusName = 'Успех';
              } else if (vacancy.result_candidate === 'failure') {
                statusName = 'Провал';
              } else if (vacancy.result_candidate === 'reserved') {
                statusName = 'В резерве';
              }
            }

            return {
              value: `${vacancy.vacancy_id}-${vacancy.status_id}`,
              label: `${vacancyName} - ${statusName}`
            };
          });

          return (
            <Select
              style={{ width: '100%', minWidth: 200 }}
              value={vacancyOptions[0]?.value}
              options={vacancyOptions}
              placeholder="Выберите вакансию"
            // disabled={vacancyOptions.length === 1}
            />
          );
        }

        if (candidateVacancyData) {
          const candidateVacancy = candidateVacancyData.find(cv => cv.candidate_id === record.id);
          if (candidateVacancy) {
            const vacancyName = getVacancyName(candidateVacancy.vacancy_id);
            let statusName = getStatusName(candidateVacancy.status_id);
            const statusKey = getStatusKeyFromName(statusName);

            // Если статус "Завершить", показываем результат кандидата
            if (statusKey === 'hired' && candidateVacancy.result_candidate) {
              if (candidateVacancy.result_candidate === 'success') {
                statusName = 'Успех';
              } else if (candidateVacancy.result_candidate === 'failure') {
                statusName = 'Провал';
              } else if (candidateVacancy.result_candidate === 'reserved') {
                statusName = 'В резерве';
              }
            }

            return (
              <Select
                style={{ width: '100%', minWidth: 200 }}
                value={`${candidateVacancy.vacancy_id}-${candidateVacancy.status_id}`}
                options={[{
                  value: `${candidateVacancy.vacancy_id}-${candidateVacancy.status_id}`,
                  label: `${vacancyName} - ${statusName}`
                }]}
                disabled
              />
            );
          }
        }

        return <span>Нет данных о вакансиях</span>;
      }
    };

    const baseCols = visibleColumns
      .filter(key => key !== 'actions' && key && (columnLabels[key] || key === 'vacancy_and_status'))
      .map((key) => ({
        title: key === 'vacancy_and_status' ? (isVacancyContext ? 'Статус' : 'Вакансия и статус') : columnLabels[key],
        dataIndex: key,
        key,
        render: columnRenderMap[key] || ((text) => text),
        ellipsis: false,
        width: key === 'id' ? 70 : key === 'vacancy_and_status' && isVacancyContext ? 220 : key === 'files' ? 180 : undefined,
        style: {
          wordBreak: key === 'files' ? 'keep-all' : 'break-word',
          whiteSpace: key === 'files' ? 'nowrap' : 'normal',
          maxWidth: key === 'id' ? 60 : key === 'vacancy_and_status' ? 250 : key === 'files' ? 180 : 200,
          overflow: key === 'vacancy_and_status' ? 'visible' : 'hidden',
          textOverflow: key === 'vacancy_and_status' ? 'clip' : 'ellipsis'
        },
        onCell: () => ({
          style: {
            wordBreak: key === 'files' ? 'keep-all' : 'break-word',
            whiteSpace: key === 'files' ? 'nowrap' : 'normal',
            maxWidth: key === 'id' ? 60 : key === 'vacancy_and_status' ? 250 : key === 'files' ? 180 : 200,
            overflow: key === 'vacancy_and_status' ? 'visible' : 'hidden',
            textOverflow: key === 'vacancy_and_status' ? 'clip' : 'ellipsis'
          }
        }),
        filteredValue: isModalMode
          ? (modalFilters[key] !== undefined ? (key === 'status' && Array.isArray(modalFilters[key]) ? modalFilters[key] : [modalFilters[key]]) : null)
          : (tempFilters[key] ? (key === 'status' ? (Array.isArray(tempFilters[key]) ? tempFilters[key] : [tempFilters[key]]) : [tempFilters[key]]) : null),
        sorter: key === 'id' ? (a, b) => a.id - b.id : undefined,
        onFilter: isModalMode ? (value, record) => {
          if (key === 'status') {
            // Для статуса нужно проверить статус кандидата в вакансии
            if (isVacancyContext && currentVacancy && candidateVacancyData) {
              const candidateVacancy = candidateVacancyData.find(cv => cv.candidate_id === record.id);
              if (candidateVacancy) {
                return String(candidateVacancy.status_id) === String(value);
              }
            }
            // Для обычного режима проверяем статус из vacancies
            if (record.vacancies && Array.isArray(record.vacancies) && record.vacancies.length > 0) {
              return String(record.vacancies[0].status_id) === String(value);
            }
            return false;
          }
          if (key === 'completion_work') {
            // Для даты завершения нужно получить правильное значение
            let dateValue = record[key];
            if (isVacancyContext && currentVacancy && candidateVacancyData) {
              const candidateVacancy = candidateVacancyData.find(vc =>
                vc.candidate_id === record.id && vc.vacancy_id === currentVacancy.id
              );
              if (candidateVacancy?.completion_work) {
                dateValue = candidateVacancy.completion_work;
              }
            }
            if (!dateValue) return false;
            // Форматируем дату для поиска
            const formattedDate = new Date(dateValue).toLocaleDateString('ru-RU');
            return formattedDate.toLowerCase().includes(String(value).toLowerCase());
          }
          const cellValue = record[key];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(String(value).toLowerCase());
        } : false,
        filters: isModalMode ? (() => {
          if (key === 'status') {
            // Собираем уникальные статусы из записей
            const statusSet = new Set();
            candidates.forEach(candidate => {
              if (isVacancyContext && currentVacancy && candidateVacancyData) {
                const candidateVacancy = candidateVacancyData.find(cv => cv.candidate_id === candidate.id);
                if (candidateVacancy && candidateVacancy.status_id) {
                  statusSet.add(String(candidateVacancy.status_id));
                }
              } else if (candidate.vacancies && Array.isArray(candidate.vacancies) && candidate.vacancies.length > 0) {
                if (candidate.vacancies[0].status_id) {
                  statusSet.add(String(candidate.vacancies[0].status_id));
                }
              }
            });
            return Array.from(statusSet).map(statusId => {
              const statusName = getStatusName(statusId);
              return {
                text: statusName,
                value: statusId
              };
            });
          }
          return undefined;
        })() : undefined,
        filterDropdown: isModalMode ? (key === 'status' ? null : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          return (
            <div style={{ padding: 8 }}>
              <Input
                placeholder={`Поиск в ${key === 'vacancy_and_status' ? 'Вакансии и статусе' : columnLabels[key]}`}
                value={selectedKeys?.[0] || modalFilters[key] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedKeys(value ? [value] : []);
                }}
                onPressEnter={() => {
                  const filterValue = selectedKeys?.[0] || '';
                  setModalFilters(prev => ({
                    ...prev,
                    [key]: filterValue || null
                  }));
                  confirm();
                }}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
                allowClear
                onClear={() => {
                  setSelectedKeys([]);
                  setModalFilters(prev => {
                    const newFilters = { ...prev };
                    delete newFilters[key];
                    return newFilters;
                  });
                  clearFilters();
                  confirm();
                }}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    const filterValue = selectedKeys?.[0] || '';
                    setModalFilters(prev => ({
                      ...prev,
                      [key]: filterValue || null
                    }));
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Применить
                </Button>
                <Button
                  onClick={() => {
                    setSelectedKeys([]);
                    setModalFilters(prev => {
                      const newFilters = { ...prev };
                      delete newFilters[key];
                      return newFilters;
                    });
                    clearFilters();
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Сброс
                </Button>
              </Space>
            </div>
          );
        }) : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          const handleApply = () => {
            const newTempFilters = { ...tempFilters };
            if (selectedKeys && selectedKeys.length > 0) {
              if (key === 'status') {
                newTempFilters[key] = selectedKeys;
              } else {
                newTempFilters[key] = selectedKeys[0];
              }
            } else {
              delete newTempFilters[key];
            }
            setTempFilters(newTempFilters);
            confirm();
          };

          const handleClear = () => {
            const newTempFilters = { ...tempFilters };
            delete newTempFilters[key];
            setTempFilters(newTempFilters);
            clearFilters();
            // Если это был последний фильтр, автоматически сбрасываем все фильтры
            if (Object.keys(newTempFilters).length === 0) {
              handleResetFilters();
            }
            confirm();
          };

          if (key === 'status') {
            return (
              <div style={{ padding: 6 }}>
                <Select
                  placeholder="Выберите статусы"
                  value={selectedKeys}
                  onChange={(value) => setSelectedKeys(value || [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  mode="multiple"
                  options={statusOptions}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          if (key === 'files') {
            return (
              <div style={{ padding: 6 }}>
                <Select
                  placeholder="Выберите фильтр"
                  value={selectedKeys[0]}
                  onChange={(value) => setSelectedKeys(value ? [value] : [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  options={[
                    { value: 'has_files', label: 'Есть файлы' },
                    { value: 'no_files', label: 'Нет файлов' }
                  ]}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          return (
            <div style={{ padding: 8 }}>
              <Input
                placeholder={`Поиск в ${key === 'vacancy_and_status' ? 'Вакансии и статусе' : columnLabels[key]}`}
                value={selectedKeys[0]}
                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={handleApply}
                  size="small"
                  style={{ width: 90 }}
                >
                  Применить
                </Button>
                <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                  Сброс
                </Button>
              </Space>
            </div>
          );
        },
        filterIcon: (filtered) => {
          if (isModalMode) {
            const hasFilter = modalFilters[key] !== undefined && modalFilters[key] !== null && modalFilters[key] !== '';
            return <SearchOutlined style={{ color: (filtered || hasFilter) ? '#1890ff' : undefined }} />;
          }
          const hasTempFilter = tempFilters[key] !== undefined && tempFilters[key] !== null && tempFilters[key] !== '';
          return <SearchOutlined style={{ color: hasTempFilter ? '#1890ff' : undefined }} />;
        },
      }));

    const includeActions = visibleColumns.includes('actions');
    return includeActions ? [...baseCols, actionColumn] : baseCols;
  }, [visibleColumns, candidateVacancyData, funnelStatuses, funnelStatusesLoading, tempFilters, modalFilters, getVacancyName, getStatusName, isSelectMode, isCandidatesPage, onSelect, onUpdate, isVacancyContext, currentVacancy, funnelConfig, onStatusChange, onReturnToWork, canEditCandidate, navigate, handleApplyFilters, hasTempFiltersForTitle, isModalMode, candidates]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <div style={{
      '--table-cell-word-break': 'break-word',
      '--table-cell-white-space': 'normal'
    }}>
      {!isModalMode && hasActiveFilters && (
        <div style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '8px 0'
        }}>
          <Button
            icon={<ClearOutlined />}
            onClick={handleResetFilters}
            size="middle"
          >
            Сбросить фильтры
          </Button>
        </div>
      )}
      {isModalMode && (
        <div style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 0'
        }}>
          <span style={{ color: '#666' }}>
            Показано {displayedCandidates?.length || 0} из {filteredCandidates?.length || 0} записей {Object.keys(modalFilters).length > 0 && `(всего: ${candidates?.length || 0})`}
          </span>
          {hasMoreCandidates && (
            <Button
              type="primary"
              onClick={handleShowMore}
            >
              Показать еще 10
            </Button>
          )}
        </div>
      )}
      <Table
        columns={generateColumns || []}
        dataSource={Array.isArray(displayedCandidates) ? displayedCandidates : []}
        rowKey="id"
        bordered
        size="middle"
        locale={{
          triggerDesc: 'Нажмите для сортировки по убыванию',
          triggerAsc: 'Нажмите для сортировки по возрастанию',
          cancelSort: 'Сбросить сортировку',
        }}
        loading={loading}
        pagination={isModalMode ? false : (currentPage && totalPages !== undefined && onPageChange ? {
          current: currentPage,
          pageSize: pageSize,
          total: totalItems !== undefined && totalItems !== null ? totalItems : (totalPages * pageSize),
          showTotal: (total, range) => {
            const actualTotal = totalItems !== undefined && totalItems !== null ? totalItems : total;
            const endRange = Math.min(range[1], actualTotal);
            return `${range[0]}-${endRange} из ${actualTotal} записей`;
          },
          showSizeChanger: showSizeChanger,
          showQuickJumper: false,
          showLessItems: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, size) => {
            const newSize = size !== undefined ? size : pageSize;
            onPageChange(page, newSize);
          },
          onShowSizeChange: (current, size) => {
            onPageChange(1, size);
          },
        } : {
          pageSize: pageSize,
          showTotal: (total, range) => {
            const actualTotal = candidates.length;
            const endRange = Math.min(range[1], actualTotal);
            return `${range[0]}-${endRange} из ${actualTotal} записей`;
          },
          showQuickJumper: false,
          defaultPageSize: pageSize
        })}
        scroll={{ x: 'max-content' }}
        onChange={isModalMode ? (pagination, tableFilters, sorter) => {
          // Обновляем modalFilters при изменении фильтров через filters (статус)
          const newModalFilters = { ...modalFilters };
          Object.keys(tableFilters).forEach(filterKey => {
            if (tableFilters[filterKey] && tableFilters[filterKey].length > 0) {
              if (filterKey === 'status') {
                newModalFilters[filterKey] = tableFilters[filterKey];
              } else {
                newModalFilters[filterKey] = tableFilters[filterKey][0];
              }
            } else {
              delete newModalFilters[filterKey];
            }
          });
          setModalFilters(newModalFilters);
        } : handleTableChange}
        onRow={(candidate) => ({
          onDoubleClick: () => {
            // if (!location.pathname.includes('/candidates')) {
            //   navigate(`/candidates?openCandidate=${candidate.id}`);
            // } else {
            //   onView(candidate)
            // }
            navigate(`/candidates/${candidate.id}`);
          },
          style: { cursor: 'pointer' }
        })}
        className="word-wrap-table"
      />
      <style jsx>{`
        .word-wrap-table .ant-table-cell {
          word-break: break-word !important;
          white-space: normal !important;
          vertical-align: top !important;
          overflow: visible !important;
        }
        .word-wrap-table .ant-table-cell.actions-column {
          word-break: keep-all !important;
          white-space: nowrap !important;
        }
        .word-wrap-table .ant-table-cell[data-index="id"] {
          max-width: 60px !important;
        }
        .word-wrap-table .ant-table-cell[data-index="vacancy_and_status"] {
          min-width: 220px !important;
        }
        .word-wrap-table .ant-table-cell[data-index="files"] {
          max-width: 180px !important;
          word-break: keep-all !important;
          white-space: nowrap !important;
          overflow: hidden !important;
        }
      `}</style>
    </div>
  );
};

export default CandidateList;